import type { ReactNode } from 'react';
import { Gauge, Link2, TrendingUp } from 'lucide-react';
import { BrandIcon, MetricTile } from '@/components/ui';
import { SeoMetricsCard } from '@/components/sites/SeoMetricsCard';
import { SeoTimelineCard } from '@/components/sites/SeoTimelineCard';
import { useSeoTimeline, useSiteSeoMetrics } from '@/hooks/useSeoMetrics';
import { deltaOf, number, pointsOf, trendOf } from '@/utils/seo-trends';

const AHREFS = (
  <>
    <TrendingUp size={12} aria-hidden="true" />
    Ahrefs
  </>
);
const DATAFORSEO = (
  <>
    <Link2 size={12} aria-hidden="true" />
    DataForSEO
  </>
);
const OPR = (
  <>
    <Gauge size={12} aria-hidden="true" />
    Open PageRank
  </>
);
const ARCHIVE = (
  <>
    <BrandIcon name="internetarchive" size={12} />
    Web Archive
  </>
);

/**
 * The domain as the link graph sees it (ADR-0057), the way Ahrefs's overview
 * shows a site: each figure large, how it moved in a month, and a small line
 * of its year; then the chart to look closer, and each source's state.
 */
export function SeoMetricsOverview({ siteId }: { readonly siteId: string }): ReactNode {
  const metrics = useSiteSeoMetrics(siteId);
  const timeline = useSeoTimeline(siteId);
  const view = metrics.data;
  const ahrefs = view?.ahrefs?.data ?? null;
  const dfs = view?.dataforseo?.data ?? null;
  const opr = view?.openpagerank?.data ?? null;
  const wayback = view?.wayback?.data ?? null;

  const dr = trendOf(pointsOf(timeline.data, 'domain_rating', ['ahrefs']));
  const refs = trendOf(pointsOf(timeline.data, 'referring_domains'));
  const traffic = trendOf(pointsOf(timeline.data, 'organic_traffic', ['ahrefs']));
  const backlinks = trendOf(pointsOf(timeline.data, 'backlinks', ['dataforseo']));
  const pageRank = trendOf(pointsOf(timeline.data, 'page_rank', ['openpagerank']));
  const refSource = timeline.data?.series.find((line) => line.metric === 'referring_domains' && line.source === 'ahrefs')
    ? AHREFS
    : dfs?.referringDomains !== undefined && dfs?.referringDomains !== null
      ? DATAFORSEO
      : OPR;
  const spam = dfs ? (dfs.spamScore ?? dfs.backlinksSpamScore) : null;
  const firstYear = wayback?.years[0] ?? null;
  const month = 'за 30 дней';

  return (
    <div className="stack">
      <div className="metric-tiles">
        <MetricTile
          label="Domain Rating"
          source={AHREFS}
          value={number(ahrefs?.domainRating ?? dr.current, 1)}
          unit="из 100"
          delta={deltaOf(ahrefs?.domainRating ?? dr.current, dr.before, { digits: 1 })}
          history={dr.history}
          footnote={dr.before !== null ? month : ahrefs ? 'история копится' : 'подключите Ahrefs'}
        />
        <MetricTile
          label="Ссылающиеся домены"
          source={refSource}
          value={number(ahrefs?.referringDomains ?? dfs?.referringDomains ?? opr?.referringDomains ?? refs.current)}
          delta={deltaOf(refs.current, refs.before)}
          history={refs.history}
          footnote={refs.before !== null ? month : undefined}
        />
        <MetricTile
          label="Обратные ссылки"
          source={DATAFORSEO}
          value={number(dfs?.backlinks ?? backlinks.current)}
          delta={deltaOf(backlinks.current, backlinks.before)}
          history={backlinks.history}
          footnote={dfs ? (backlinks.before !== null ? month : undefined) : 'подключите DataForSEO'}
        />
        <MetricTile
          label="Органический трафик"
          source={AHREFS}
          value={number(ahrefs?.organicTraffic ?? traffic.current)}
          unit="в мес."
          delta={deltaOf(traffic.current, traffic.before, { percent: true })}
          history={traffic.history}
          footnote={traffic.before !== null ? month : ahrefs ? 'нужна история Ahrefs' : 'подключите Ahrefs'}
        />
        <MetricTile
          label="PageRank"
          source={OPR}
          value={opr && !opr.found ? 'нет в графе' : number(opr?.pageRank ?? pageRank.current, 2)}
          unit={opr?.found ? 'из 10' : undefined}
          delta={deltaOf(pageRank.current, pageRank.before, { digits: 2 })}
          history={pageRank.history}
          footnote={opr?.rank ? `место ${opr.rank.toLocaleString('ru-RU')}` : opr ? undefined : 'подключите Open PageRank'}
        />
        <MetricTile
          label="Спам-скор"
          source={DATAFORSEO}
          value={number(spam)}
          unit={spam !== null ? 'из 100' : undefined}
          better="down"
          footnote={spam === null ? (dfs ? undefined : 'подключите DataForSEO') : spam >= 60 ? 'высокий' : spam >= 30 ? 'повышенный' : 'низкий'}
        />
        <MetricTile
          label="В Web Archive"
          source={ARCHIVE}
          value={firstYear ? `с ${firstYear}` : wayback ? 'нет снимков' : null}
          footnote={wayback && wayback.years.length > 0 ? `лет в архиве: ${wayback.years.length}` : undefined}
        />
      </div>

      <SeoTimelineCard siteId={siteId} metrics={['domain_rating', 'referring_domains', 'organic_traffic', 'page_rank', 'backlinks']} title="Динамика ссылок и трафика" />
      <SeoMetricsCard siteId={siteId} />
    </div>
  );
}
